<template>
  <ImageValidation
    :platform-validation="platformValidation"
    :is-validating="isValidating"
  ></ImageValidation>
</template>

<script lang="ts" setup>
import { computed, inject, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRouter, onBeforeRouteLeave, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'

import ImageValidation from '@/components/ImageValidation.vue'

import { useUploadedCreativeStore } from '@/stores/uploadedCreative'
import { useBrandStore } from '@/stores/brand'
import { usePlacementStore } from '@/stores/placement'
import { useAppStore } from '@/stores/app'
import { useCanvasStore } from '@/stores/canvas'
import { useAssetStore } from '@/stores/asset'
import designFlows from '@/constants/designFlows'
import CreativeService from '@/services/creativeService'
import type { ValidateRequest } from '@/types/validateRequest'
import type { Validation } from '@/types/validation'
import { autoFixCreative } from '@/helpers/creativeHelper'
import type { FooterConfigProvider } from '@/types/footerConfig'
import { useEditorStore } from '@/stores/editor'
import { Canvas } from 'fabric'
import { loadCanvasData } from '@/helpers/canvasHelper'
import { useAutofixStore } from '@/stores/autofix'

const { t } = useI18n()
const uploadedCreativeStore = useUploadedCreativeStore()
const brandStore = useBrandStore()
const placementStore = usePlacementStore()
const editorStore = useEditorStore()
const appStore = useAppStore()
const canvasStore = useCanvasStore()
const assetStore = useAssetStore()
const autofixStore = useAutofixStore()
const creativeService = new CreativeService()
const router = useRouter()
const route = useRoute()

const notify = inject('notify') as Function
const { footerConfig, setFooterConfig } = inject<FooterConfigProvider>('footerConfig')!

const isValidating = ref<boolean>(false)
const isAutoFixing = ref<boolean>(false)
const isEditloading = ref<boolean>(false)
const isNextloading = ref<boolean>(false)
const disableAutoFix = ref<boolean>(false)
const disableBack = ref<boolean>(false)

const platformValidation = computed({
  get: () => uploadedCreativeStore.validations,
  set: (value) => {
    uploadedCreativeStore.setValidations(value)
  },
})

const hasValidationFailures = computed(() =>
  platformValidation.value.some(
    (validation: { status: string }) =>
      validation.status === 'fail' || validation.status === 'partial',
  ),
)

const disableNext = computed(
  () =>
    isValidating.value ||
    isAutoFixing.value ||
    !platformValidation.value.length ||
    hasValidationFailures.value,
)

const handleAutoFix = async () => {
  try {
    isAutoFixing.value = true
    router.push({ name: 'Convert' })
  } catch (error) {
    console.error(t('errors.somethingWentWrong'), error)
  }
}

const handleNext = async () => {
  isNextloading.value = true
  if (!hasValidationFailures.value && !canvasStore.canvasJson) {
    const fabricCanvas = new Canvas(undefined, {
      width: 1141,
      height: 719,
      preserveObjectStacking: true,
      selection: false,
    })
    if (placementStore.selectedPublishedVariant?.canvasJson) {
      await loadCanvasData(
        fabricCanvas,
        placementStore.selectedPublishedVariant.canvasJson,
        '#FFFFFF',
      )
    }
    canvasStore.setFabricCanvas(fabricCanvas)
    if (placementStore.selectedPublishedVariant) {
      canvasStore.setCanvasJson(placementStore.selectedPublishedVariant.canvasJson)
    }
    // await autoFixCreative()
  }
  assetStore.setCreativeS3Url(uploadedCreativeStore.uploadedCreativeUrl)
  appStore.incrementStepperCount()
  isNextloading.value = false
  router.push({ name: 'Download' })
}

const abortController = new AbortController()

const goToEditor = async () => {
  isEditloading.value = true
  if (!hasValidationFailures.value && !canvasStore.canvasJson) {
    await autoFixCreative(abortController.signal)
  }
  isEditloading.value = false
  router.push({ name: 'Editor' })
}

const validateCreative = async () => {
  try {
    if (uploadedCreativeStore.uploadedCreativeUrl) {
      isValidating.value = true
      disableBack.value = true
      const requestData: ValidateRequest = {
        brandName: brandStore.selectedBrand?.name || '',
        adImageUrl: uploadedCreativeStore.uploadedCreativeUrl,
        canvasJson: JSON.stringify(placementStore.selectedPublishedVariant?.canvasJson),
        guidelinesData: JSON.stringify(placementStore.selectedPlacement?.creativeSetGuidelines),
        logoUrl: brandStore.selectedLogo
          ? brandStore.selectedLogo.logoUrl
          : '',
      }
      const result = await creativeService.validateCreative(requestData)
      if (result.status === 500 || result.data === undefined) {
        throw new Error(result.message)
      }
      autofixStore.detectionsResponse = result.data.detections
      const responseData = result.data.validations.brand_guidelines_validation
      platformValidation.value =
        responseData.filter(
          (item: Validation, index: number, self: Validation[]) =>
            self.findIndex((t: Validation) => t.name === item.name) === index,
        ) || []

      const noProducts = platformValidation.value.filter(
        (validation: Validation) =>
          validation.name.toLowerCase() === 'Product Detection'.toLowerCase() &&
          validation.status === 'fail' &&
          validation.details.toLowerCase() ===
            'No product elements detected in the design.'.toLowerCase(),
      )
      if (noProducts.length) {
        disableAutoFix.value = true
      }
    }
  } catch {
    notify(t('errors.somethingWentWrong'), 'error')
  } finally {
    isValidating.value = false
    disableBack.value = false
  }
}

watch(disableNext, (newVal) => {
  footerConfig.disableNext = newVal
})

watch(platformValidation, () => {
  setFooterConfig({
    showAutoFix: hasValidationFailures.value,
    showEdit: !hasValidationFailures.value,
  })
})

watch(isNextloading, (newVal) => {
  footerConfig.loadingNext = newVal
})

watch(isEditloading, (newVal) => {
  footerConfig.loadingEdit = newVal
})

watch(isAutoFixing, (newVal) => {
  footerConfig.loadingAutoFix = newVal
})

watch(disableAutoFix, (newVal) => {
  footerConfig.disableAutoFix = newVal
})

watch(disableBack, (newvalue) => {
  footerConfig.disableBack = newvalue
})

onMounted(() => {
  setFooterConfig({
    showNext: true,
    showBack: true,
    showAutoFix: hasValidationFailures.value,
    showEdit: !!platformValidation.value.length && !hasValidationFailures.value,
    disableNext: disableNext.value,
    disableBack: disableBack.value,
    disableAutoFix: disableAutoFix.value,
    loadingNext: isNextloading.value,
    loadingEdit: isEditloading.value,
    loadingAutoFix: isAutoFixing.value,
    onNext: handleNext,
    onAutoFix: handleAutoFix,
    onEdit: goToEditor,
  })
  window.onbeforeunload = () => {
    if (route.name === 'Validate') return ''
  }
  if (
    appStore.selectedDesignFlow.value === designFlows.upload.value &&
    !platformValidation.value.length
  ) {
    validateCreative()
  }
  if (appStore.stepperCount != 1) {
    appStore.setStepperCount(1)
  }
})

onUnmounted(() => {
  setFooterConfig({
    showNext: false,
    showBack: false,
    showAutoFix: false,
    showEdit: false,
    disableNext: true,
    disableAutoFix: true,
    loadingNext: false,
    loadingEdit: false,
    loadingAutoFix: false,
    onNext: () => {},
    onAutoFix: () => {},
    onEdit: () => {},
  })
  window.onbeforeunload = null
})

onBeforeRouteLeave((to, from, next) => {
  if (to.name === 'Mode') {
    const answer = window.confirm('You have unsaved changes. Are you sure you want to leave?')
    if (answer) {
      abortController.abort()
      autofixStore.resetAutofixState()
      editorStore.isAutoFix = false
      next()
    } else {
      next(false)
    }
  } else {
    next()
  }
})
</script>
<style scoped>
:deep(.v-list-item__prepend > .v-icon) {
  opacity: 1 !important;
}
</style>
